import { lazy, Suspense, type ReactElement } from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom'

import { QueryClientProvider } from '@tanstack/react-query'
import { AppLayout } from '@/components/layouts/app-layout'
import { PortalLayout } from '@/components/layouts/portal-layout'
import { Skeleton } from '@/components/ui/skeleton'
import { Toaster } from '@/components/ui/sonner'
import { BackgroundUploadIndicator } from '@/components/background-upload-indicator'
import { queryClient } from '@/lib/query-client'
import { ProtectedRoute } from '@/routes/protected-route'
import { AdminRoute } from '@/routes/admin-route'
import { PortalRoute } from '@/routes/portal-route'

// Route-level code-splitting (Story 2.12 — MNT-001). Each page is a lazy
// chunk so the initial app entry stays small; `vite.config.ts` `manualChunks`
// splits the third-party vendor code on top of this.
const SignInPage = lazy(() => import('@/routes/sign-in').then((m) => ({ default: m.SignInPage })))
// Story 15.1 (Epic 15) — a segunda tela PÚBLICA: quem chega aqui vem de um
// email de convite e ainda não tem conta. Fica fora do ProtectedRoute pela
// mesma razão do sign-in — exigir sessão para criar a sessão seria um ciclo.
const AcceptInvitePage = lazy(() =>
  import('@/routes/accept-invite').then((m) => ({ default: m.AcceptInvitePage })),
)
// Migration 0040 — a TERCEIRA tela pública. Fora do ProtectedRoute pela mesma
// razão das duas acima, e por uma mais forte: quem chega aqui não consegue
// entrar, e é exatamente isso que o traz.
const ResetPasswordPage = lazy(() =>
  import('@/routes/reset-password').then((m) => ({ default: m.ResetPasswordPage })),
)
// #543 — o «Esqueci minha senha» do /sign-in: pede o link que a tela de cima
// consome. Pública pela mesma razão dela.
const ForgotPasswordPage = lazy(() =>
  import('@/routes/forgot-password').then((m) => ({ default: m.ForgotPasswordPage })),
)
// Story 16.1 — a QUARTA tela pública, e a única que não é sobre autenticação:
// o catálogo de uma vaga, aberto por quem recebeu o link. Ver o comentário na
// rota, mais abaixo.
const PublicCatalogPage = lazy(() =>
  import('@/routes/catalog').then((m) => ({ default: m.PublicCatalogPage })),
)
const DashboardPage = lazy(() =>
  import('@/routes/dashboard').then((m) => ({ default: m.DashboardPage })),
)
const AccountManagementDashboardPage = lazy(
  () => import('@/routes/_authenticated/account-management'),
)
const NotFoundPage = lazy(() =>
  import('@/routes/not-found').then((m) => ({ default: m.NotFoundPage })),
)
const AssessmentsListPage = lazy(() => import('@/routes/_authenticated/assessments'))
const NewAssessmentPage = lazy(() => import('@/routes/_authenticated/assessments/new'))
const AssessmentEditorPage = lazy(() => import('@/routes/_authenticated/assessments/editor'))
// Story 13.1 (Epic 13) — practical assessment detail page. Fixes the 404 when a
// practical row is clicked (onRowClick navigates to /assessments/:id without
// /editor). Interview rows that land here are redirected to /editor.
const PracticalAssessmentDetailPage = lazy(
  () => import('@/routes/_authenticated/assessments/detail'),
)
const InterviewTranscriptPage = lazy(() => import('@/routes/_authenticated/evaluations/transcript'))
const NewEvaluationPage = lazy(() => import('@/routes/_authenticated/evaluations/new'))
const EvaluationDetailPage = lazy(() => import('@/routes/_authenticated/evaluations/detail'))
const OpportunitiesListPage = lazy(() => import('@/routes/_authenticated/opportunities'))
const OpportunityFormPage = lazy(() => import('@/routes/_authenticated/opportunities/form'))
// Migration 0022 — o cliente (a empresa para quem se recruta) e o perfil de
// vaga (o molde de um cargo). Ambos são catálogo: cadastram-se uma vez e as
// vagas pendem deles.
const CustomersListPage = lazy(() => import('@/routes/_authenticated/customers'))
const CustomerFormPage = lazy(() => import('@/routes/_authenticated/customers/form'))
const JobProfilesListPage = lazy(() => import('@/routes/_authenticated/job-profiles'))
const JobProfileFormPage = lazy(() => import('@/routes/_authenticated/job-profiles/form'))
const ApplicationsListPage = lazy(() => import('@/routes/_authenticated/applications'))
const ApplicationFormPage = lazy(() => import('@/routes/_authenticated/applications/form'))
const ApplicationResumesPage = lazy(() => import('@/routes/_authenticated/applications/resumes'))
const ProfessionalsListPage = lazy(() => import('@/routes/_authenticated/professionals'))
const ProfessionalHubPage = lazy(() => import('@/routes/_authenticated/professionals/hub'))
// Story 11.1 (Epic 11) — professional profile edit page.
const ProfessionalEditPage = lazy(() => import('@/routes/_authenticated/professionals/edit'))
// Task 9 — professional registration page (same form as the edit page).
const ProfessionalNewPage = lazy(() => import('@/routes/_authenticated/professionals/new'))
// Visualizador do CV EXTERNO do profissional (o que ele próprio enviou) — tela
// própria porque um currículo se lê inteiro; ver o cabeçalho de `cv.tsx`.
const ProfessionalCvPage = lazy(() => import('@/routes/_authenticated/professionals/cv'))
// Onda W1 — o Caderno do profissional: os apontamentos deixaram de pertencer à
// alocação e passaram a pertencer à PESSOA. Substitui, do lado da Genesis, o
// diálogo pendurado na alocação — ver o cabeçalho de `caderno.tsx`.
const ProfessionalCadernoPage = lazy(() => import('@/routes/_authenticated/professionals/caderno'))
// Story 31.7 — a fila de triagem da função e das skills.
const ProfessionalsTriagemPage = lazy(() => import('@/routes/_authenticated/professionals/triagem'))
// Story 10.1 (Epic 10) — user settings page (profile name + appearance).
const SettingsPage = lazy(() => import('@/routes/_authenticated/settings'))
// Story 10.2 (Epic 10) — admin user management (gated by AdminRoute below).
const SettingsUsersPage = lazy(() => import('@/routes/_authenticated/settings-users'))
// #485 — o catálogo de skills e a fusão (atrás do AdminRoute, como Usuários).
const SettingsSkillsPage = lazy(() => import('@/routes/_authenticated/settings-skills'))
// #593 — a trilha das escritas (atrás do AdminRoute, como Usuários).
const SettingsAuditPage = lazy(() => import('@/routes/_authenticated/settings-audit'))
// Migration 0023 (onda W1) — cadastro do processo seletivo. Vive sob /settings
// porque é configuração do produto, mas o item do trilho mora no Catálogo: é o
// que a vaga referencia, como o cliente e o perfil.
// Onda W4 — o quadro. GLOBAL com seletor de vaga (`?opportunityId=`), e não uma
// aba dentro da vaga: quem o usa salta de vaga em vaga o dia inteiro.
const KanbanPage = lazy(() => import('@/routes/_authenticated/kanban'))
const PipelinesListPage = lazy(() => import('@/routes/_authenticated/settings/pipelines'))
const PipelineFormPage = lazy(() => import('@/routes/_authenticated/settings/pipelines/form'))
// F1 do PORTAL DO CLIENTE (spec de 2026-08-05) — a tela de entrada do cliente.
// Árvore própria, com shell próprio: ver o bloco de rotas lá em baixo.
const PortalHomePage = lazy(() => import('@/routes/portal'))
// F2 do portal — os profissionais alocados e os apontamentos. F3 — a vitrine.
// Os ARQUIVOS e a API (`/api/portal/allocations`, `/api/portal/demands`) guardam
// os nomes da spec do portal; os CAMINHOS do SPA são pt-BR desde a Story 24.2
// (`/portal/profissionais`, `/portal/vagas`), porque o cliente os lê na barra de
// endereço — e "demanda" e "alocado" são palavras de quem fornece. Os antigos
// redirecionam (`RedirectPortalPrefix`).
const PortalAllocationsPage = lazy(() => import('@/routes/portal/allocations'))
const PortalAllocationDetailPage = lazy(() => import('@/routes/portal/allocations/detail'))
const PortalDemandsPage = lazy(() => import('@/routes/portal/demands'))
const PortalShowcasePage = lazy(() => import('@/routes/portal/demands/showcase'))
// Story 38.7 — as posições das operações da conta.
const PortalPositionsPage = lazy(() => import('@/routes/portal/positions'))

/**
 * O detalhe da vaga saiu (Story 23.7, pedido do owner: «não precisamos ter tela
 * de visualização da vaga, pode ser direto a tela de editar»). O que ele tinha —
 * o interruptor do catálogo e as candidaturas — foi para a edição. Links antigos
 * (a busca global, favoritos) continuam a apontar para `/opportunities/:id`; este
 * redirecionamento é o que os mantém vivos. `replace`, pela razão de baixo.
 */
function RedirectToOpportunityEdit(): ReactElement {
  const { pathname, search, hash } = useLocation()
  return <Navigate to={`${pathname.replace(/\/$/, '')}/edit${search}${hash}`} replace />
}

/**
 * A ponte de /candidates para /professionals.
 *
 * A pessoa deixou de se chamar "candidato" (spec 2026-08-11) e a URL acompanhou,
 * porque a barra de endereço é a única parte do código que o usuário lê. Mas
 * favoritos e links já enviados por email continuam a apontar para o caminho
 * antigo, e partir esses links seria fazer o usuário pagar pela renomeação.
 *
 * Reescreve só o prefixo e devolve o resto intacto — o `:id`, a query e o
 * fragmento — para que /candidates/abc/caderno?tab=1 caia exactamente onde caía.
 * `replace` evita que o caminho velho fique no histórico: quem carregar em
 * "voltar" não deve aterrar num redirecionamento e ser reenviado para a frente.
 */
function RedirectToProfessionals(): ReactElement {
  const { pathname, search, hash } = useLocation()
  const destino = pathname.replace(/^\/candidates/, '/professionals')
  return <Navigate to={`${destino}${search}${hash}`} replace />
}

/**
 * As pontes dos caminhos antigos do PORTAL (Story 24.2) — a mesma postura da de
 * `/candidates`: "Demandas" e "Alocados" passaram a "Vagas" e "Profissionais", e
 * os favoritos do cliente continuam a apontar para `/portal/demands…` e
 * `/portal/allocations…`. Reescreve só o prefixo; o id, a query e o fragmento
 * chegam intactos, e `replace` tira o caminho velho do histórico.
 */
function RedirectPortalPrefix({ de, para }: { de: string; para: string }): ReactElement {
  const { pathname, search, hash } = useLocation()
  return <Navigate to={`${para}${pathname.slice(de.length)}${search}${hash}`} replace />
}

/**
 * RouteFallback — skeleton shown while a lazy route chunk is downloading.
 * Skeleton-shaped (NFR-DS-2: no spinners) and theme-aware via design tokens.
 */
function RouteFallback(): ReactElement {
  return (
    <div className="space-y-6 px-10 pb-12 pt-8" aria-busy="true" aria-live="polite">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-9 w-72" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

/**
 * App — top-level providers + router.
 *
 * Public:
 *   - /sign-in
 *   - /accept-invite (Story 15.1 — define a senha a partir do token do convite)
 *   - /reset-password (0040 — redefine a senha a partir do token do link)
 *   - /forgot-password (#543 — pede o link de redefinição por email)
 *
 * Protected (gated by ProtectedRoute → /api/me):
 *   - /dashboard (and future authenticated routes share AppLayout)
 *
 * All route components are lazy-loaded (Story 2.12 — MNT-001); a Suspense
 * boundary renders a skeleton fallback while the chunk downloads.
 *
 * Catch-all: NotFound.
 *
 * `BackgroundUploadIndicator` (upload em segundo plano) is mounted as a
 * SIBLING of `<Routes>`, not inside any one route — it must keep rendering
 * (and keep its progress bar / "não feche a aba" warning visible) no matter
 * where the person navigates to after submitting an interview video, so it
 * cannot live inside a route element that unmounts on navigation. It reads
 * from `useBackgroundUploadStore` (`@/stores/background-upload-store`), a
 * Zustand module singleton — same survives-navigation pattern already used by
 * `useAssessmentEditorStore` — so no React Context/Provider was added here.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public */}
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            {/* Story 16.1 — o catálogo público de uma vaga. A QUARTA tela
                pública, e a primeira que não é sobre autenticação: as outras
                três existem para alguém CRIAR uma sessão, esta existe para
                alguém que nunca vai ter nenhuma. Fora do ProtectedRoute e
                também do PortalRoute — quem abre o link não é utilizador de
                cliente, é só alguém com o endereço. O portão é o token. */}
            <Route path="/catalog/:token" element={<PublicCatalogPage />} />

            {/* Protected */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/account-management" element={<AccountManagementDashboardPage />} />
              <Route path="/assessments" element={<AssessmentsListPage />} />
              <Route path="/assessments/new" element={<NewAssessmentPage />} />
              <Route path="/assessments/:id/editor" element={<AssessmentEditorPage />} />
              {/* Story 13.1 (Epic 13) — practical assessment detail. Registered
                  AFTER /:id/editor (2 segments) so the 1-segment :id never
                  shadows it; React Router v6 ranks by specificity, but the
                  explicit ordering keeps the intent clear. */}
              <Route path="/assessments/:id" element={<PracticalAssessmentDetailPage />} />
              {/* Story 14.3 (Epic 14) — the global evaluations list (`/evaluations`)
                  was removed: evaluations now live in context inside the application
                  detail's "Avaliações" tab (Story 14.2) and as a status column in the
                  applications list (Story 14.3). The detail/transcript/new routes below
                  remain — they are reached from the application tab + the applications
                  list "Avaliar" action. */}
              {/* Story 4.5 — create evaluation (ATS-first, from an application). */}
              <Route path="/evaluations/new" element={<NewEvaluationPage />} />
              {/* Story 7.1 (Epic 7) — full interview transcript display.
                  More specific than /:id, so it MUST precede the detail route. */}
              <Route path="/evaluations/:id/transcript" element={<InterviewTranscriptPage />} />
              {/* Story 4.6 (Epic 4) — evaluation detail (the create flow + the
                  "Avaliar" action both redirect here). */}
              <Route path="/evaluations/:id" element={<EvaluationDetailPage />} />
              {/* Story 7.4 (Epic 7) — Opportunity (vaga) management. */}
              <Route path="/opportunities" element={<OpportunitiesListPage />} />
              <Route path="/opportunities/new" element={<OpportunityFormPage />} />
              <Route path="/opportunities/:id/edit" element={<OpportunityFormPage />} />
              {/* DEPOIS de `/new` e `/:id/edit`: `/new` bateria com `/:id` se
                  viesse primeiro, e o React Router escolhe por especificidade,
                  não por ordem — mas a leitura fica mais clara assim. */}
              <Route path="/opportunities/:id" element={<RedirectToOpportunityEdit />} />
              {/* Migration 0022 — Cliente (empresa) e Perfil de vaga (molde do
                  cargo). As rotas específicas (/new) vêm ANTES das de /:id
                  pela mesma razão de /professionals/new: um :id de um segmento
                  engoliria "new" se viesse primeiro. */}
              <Route path="/customers" element={<CustomersListPage />} />
              <Route path="/customers/new" element={<CustomerFormPage />} />
              <Route path="/customers/:id/edit" element={<CustomerFormPage />} />
              <Route path="/job-profiles" element={<JobProfilesListPage />} />
              <Route path="/job-profiles/new" element={<JobProfileFormPage />} />
              <Route path="/job-profiles/:id/edit" element={<JobProfileFormPage />} />
              {/* Story 7.5a (Epic 7) — Application (candidatura) management. */}
              <Route path="/applications" element={<ApplicationsListPage />} />
              <Route path="/applications/new" element={<ApplicationFormPage />} />
              <Route path="/applications/:id/edit" element={<ApplicationFormPage />} />
              {/* Story 7.11 (Epic 7) — generated CVs for an application. */}
              <Route path="/applications/:id/resumes" element={<ApplicationResumesPage />} />
              {/* Onda W4 — o Kanban. Sem `:id` no caminho: a vaga escolhida vive
                  na query string, para o quadro ser partilhável e o "voltar"
                  fazer o que se espera. */}
              <Route path="/kanban" element={<KanbanPage />} />
              {/* Story 7.8 (Epic 7) — lista de profissionais + hub do profissional. */}
              <Route path="/professionals" element={<ProfessionalsListPage />} />
              {/* Task 9 — cadastro. MAIS específica que /:id, portanto TEM de vir
                  antes da rota do hub (senão /:id engole "new"). */}
              <Route path="/professionals/new" element={<ProfessionalNewPage />} />
              {/* Story 31.7 — a fila de triagem. MAIS específica que /:id, pela mesma
                  razão de /new: senão /:id engole "triagem". */}
              <Route path="/professionals/triagem" element={<ProfessionalsTriagemPage />} />
              {/* Story 11.1 (Epic 11) — edição do perfil. MAIS específica que
                  /:id, portanto TEM de vir antes da rota do hub. */}
              <Route path="/professionals/:id/edit" element={<ProfessionalEditPage />} />
              {/* Currículo externo do profissional — MAIS específica que /:id,
                  pela mesma razão da rota de edição. */}
              <Route path="/professionals/:id/cv" element={<ProfessionalCvPage />} />
              {/* Onda W1 — o Caderno. MAIS específica que /:id, pela mesma razão
                  das duas acima. */}
              <Route path="/professionals/:id/caderno" element={<ProfessionalCadernoPage />} />
              <Route path="/professionals/:id" element={<ProfessionalHubPage />} />
              {/* A pessoa passou a chamar-se PROFISSIONAL (spec 2026-08-11), e a
                  URL acompanhou. Estas duas rotas existem porque há favoritos
                  guardados e links já enviados por email a apontar para
                  /candidates — e nenhum deles tem culpa da renomeação.

                  O `/*` apanha tudo o que vier a seguir, incluindo caminhos que
                  ainda não existem, para esta ponte não precisar de manutenção
                  sempre que nascer uma tela nova sob /professionals. */}
              <Route path="/candidates" element={<RedirectToProfessionals />} />
              <Route path="/candidates/*" element={<RedirectToProfessionals />} />
              {/* Story 10.1 (Epic 10) — user settings (profile + appearance). */}
              <Route path="/settings" element={<SettingsPage />} />
              {/* Migration 0023 (W1) — cadastro de pipelines. `/new` antes de
                  `/:id/edit` pela mesma razão de `/professionals/new`. */}
              <Route path="/settings/pipelines" element={<PipelinesListPage />} />
              <Route path="/settings/pipelines/new" element={<PipelineFormPage />} />
              <Route path="/settings/pipelines/:id/edit" element={<PipelineFormPage />} />
              {/* Story 10.2 (Epic 10) — admin user management. AdminRoute is a
                  UX gate (renders "Sem acesso" for non-admins); the real
                  perimeter is requireAdmin on /api/users (server-side). */}
              <Route
                path="/settings/users"
                element={
                  <AdminRoute>
                    <SettingsUsersPage />
                  </AdminRoute>
                }
              />
              {/* #485 — o catálogo de skills e a fusão. O perímetro é o
                  requireAdmin de /api/skills/catalog e /api/skills/:id/merge. */}
              <Route
                path="/settings/skills"
                element={
                  <AdminRoute>
                    <SettingsSkillsPage />
                  </AdminRoute>
                }
              />
              {/* #593 — a trilha das escritas. O perímetro é o requireAdmin de
                  /api/audit-log. */}
              <Route
                path="/settings/audit"
                element={
                  <AdminRoute>
                    <SettingsAuditPage />
                  </AdminRoute>
                }
              />
            </Route>

            {/* Portal do cliente (F1) — árvore SEPARADA, de propósito.

                Não é uma rota dentro do bloco acima: o `ProtectedRoute` manda
                `role='customer'` para /portal, então pendurar /portal nele daria
                um ciclo de redirects. A guarda de cá é o `PortalRoute`, que faz
                o movimento simétrico (a Genesis que aterre aqui volta para a
                dashboard). As duas juntas são o espelho, na interface, da
                fronteira que o §4 da spec impõe na API. */}
            <Route
              element={
                <PortalRoute>
                  <PortalLayout />
                </PortalRoute>
              }
            >
              <Route path="/portal" element={<PortalHomePage />} />
              {/* F2 — profissionais. O detalhe vem DEPOIS da lista; ambos têm o
                  mesmo número de segmentos fixos, mas a ordem explícita mantém
                  a intenção legível (mesma disciplina de `/assessments`). */}
              <Route path="/portal/profissionais" element={<PortalAllocationsPage />} />
              <Route path="/portal/profissionais/:id" element={<PortalAllocationDetailPage />} />
              {/* F3 — a vitrine. Mesma disciplina: a lista antes do detalhe. */}
              <Route path="/portal/vagas" element={<PortalDemandsPage />} />
              <Route path="/portal/vagas/:id" element={<PortalShowcasePage />} />
              <Route path="/portal/posicoes" element={<PortalPositionsPage />} />
              {/* Story 24.2 — os caminhos de antes, que favoritos e links já
                  partilhados ainda usam. O `/*` apanha a lista E o detalhe. */}
              <Route
                path="/portal/allocations/*"
                element={
                  <RedirectPortalPrefix de="/portal/allocations" para="/portal/profissionais" />
                }
              />
              <Route
                path="/portal/demands/*"
                element={<RedirectPortalPrefix de="/portal/demands" para="/portal/vagas" />}
              />
            </Route>

            {/* Default + 404.

                `/` continua a apontar para a dashboard mesmo para um cliente: o
                `ProtectedRoute` reencaminha-o daí para /portal no mesmo tique,
                já com o papel lido. Resolver o papel AQUI exigiria uma terceira
                guarda só para o caso de a sessão ainda estar a carregar. */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        {/* Sibling of <Suspense>/<Routes>, inside <Router> so its "Ver avaliação"
            <Link> resolves — survives every route change (see doc comment above). */}
        <BackgroundUploadIndicator />
      </Router>
      <Toaster />
    </QueryClientProvider>
  )
}
